<template>
  <div class="h-full flex flex-col">
    <!-- 顶部头部区域 -->
    <div class="flex-shrink-0">
      <SafeAreaTopSpacer v-if="showHeader" />
      <div
        v-if="showHeader"
        class="flex items-center mt-4 px-4 relative g-header-width gap-3"
      >
        <Icon
          name="close"
          class="cursor-pointer w-20px shrink-0"
          @click="emit('close')"
        ></Icon>

        <ProgressBar :percent="data.practiceProgress" />

        <transition
          name="monster-hint"
          mode="out-in"
        >
          <span
            v-if="data.hint"
            class="monster-hint"
            :style="hintStyle"
          >
            {{ data.hint }}
          </span>
        </transition>
      </div>
    </div>

    <!-- 主内容区域 -->
    <div class="flex-1 min-h-0">
      <template v-if="data.activeFrame != null">
        <transition
          name="card-slide"
          mode="out-in"
          @afterEnter="onCardEnter"
        >
          <BossStartFrame
            v-if="data.activeFrame.type === FrameType.BossStart"
            :name="data.activeFrame.name"
            class="h-full"
            @next="onEmptyFrameTick"
            @close="emit('close')"
          />

          <ConcreteCardFrame
            v-else-if="data.activeFrame.type === FrameType.Practice"
            :key="data.cardLearnRound"
            :face="data.activeFrame.face"
            class="h-full"
            @event="onEventCollect"
            @star="onStar"
            @next="onNext"
            @close="emit('close')"
          ></ConcreteCardFrame>

          <BossCardFrame
            v-else-if="data.activeFrame.type === FrameType.Boss"
            :face="data.activeFrame.boss"
            :combo="combo"
            class="h-full"
            @event="onEventCollect"
            @star="onStar"
            @next="onNext"
            @done="onBossDone"
            @card-enter="onCardEnter"
            @close="emit('close')"
            @match-correct="onMatchCorrect"
          />
        </transition>
      </template>
    </div>

    <!-- 底部操作栏 -->
    <BottomToolbar
      v-if="showBottomActions"
      class="flex-shrink-0"
      :showLastCardGuide="data.showLastCardGuide"
      :lastCardDisabled="lastCardDisabled"
      :canEdit="canEdit"
      @lastCard="onLastCardShow"
      @hint="onHintShow"
      @edit="onCardEdit"
      @feedback="onFeedback"
    />
  </div>
</template>

<script lang="ts" setup>
import {
  UnitEventType,
  QueueType,
  type Card,
  VirtualCardType,
} from '@/types/core'
import { reactive, computed, nextTick } from 'vue'
import ConcreteCardFrame from '@/components/ConcreteCard/ConcreteCard.vue'
import BossCardFrame from '@/components/VirtualCard/Boss.vue'
import ProgressBar from '@/components/ProgressBar.vue'
import BossStartFrame from '../BossStartFrame.vue'
import { useCommonStore } from '@/stores/common-store'
import { FeedbackStar } from '@/components/ConcreteCard/common/feedback'
import { InsertQueue, FrameType, type Frame } from './queue'
import { completeReport } from '../report'
import CardDetailPopup from '@/components/CardDetailPopup/CardDetailPopup.vue'
import CardEditPopup from '@/components/CardEditPopup/CardEditPopup.vue'
import { showCardDetail } from '@/components/ConcreteCard/common'

import BottomToolbar from '../BottomToolbar.vue'

import db from '@/db'
import { vibrate, VibrateType, openLink } from '@/utils/misc'
import { useRouter } from 'vue-router'

const props = defineProps<{
  packageId?: number
}>()

const emit = defineEmits<{
  close: []
}>()

const store = useCommonStore()
const router = useRouter()

const data = reactive({
  cardLearnRound: 0,
  activeFrame: null as Frame | null,
  lastCard: null as Card | null,
  practiceProgress: 0,
  hint: '',
  // 在答题完成后会先计算是否需要展示下一张卡片的提示，存储在该字段
  preShowLastCardGuide: false,
  // 当卡片动画结束跳转到下一张卡片时再具体显示对应的提示
  showLastCardGuide: false,
})

const showHeader = computed(() => {
  return data.activeFrame?.type === FrameType.Practice
})

const showBottomActions = computed(() => {
  return (
    data.activeFrame?.type === FrameType.Practice ||
    data.activeFrame?.type === FrameType.Boss
  )
})

const lastCardDisabled = computed(() => data.lastCard == null)

const canEdit = computed(() => {
  if (!queue || !data.activeFrame) return false

  let currentCard: Card | undefined
  let cardId: number | undefined

  // 根据不同阶段获取当前卡片信息
  if (data.activeFrame.type === FrameType.Practice) {
    currentCard = data.activeFrame.face?.card
    cardId = data.activeFrame.face?.cardId
  } else if (
    data.activeFrame.type === FrameType.Boss &&
    data.activeFrame.boss.cardFace &&
    data.activeFrame.boss.cardFace.type !== VirtualCardType.EnWordMatch &&
    data.activeFrame.boss.cardFace.type !== VirtualCardType.KidHanziMatch
  ) {
    const cardFace = data.activeFrame.boss.cardFace as any
    currentCard = cardFace.card
    cardId = cardFace.cardId
  }

  if (!currentCard || !cardId) return false

  // 检查是否有编辑权限 - 比较卡片作者ID和当前用户ID
  const cardSchedule = store.stageUnit?.schedules?.find(
    s => s.cardId === cardId
  )
  if (!cardSchedule || !store.user?.id) return false

  return store.user.id === cardSchedule.authorId
})

let queue: InsertQueue
function loadUnit() {
  if (!store.checkStageUnit()) {
    _global.unreachable(store.setStageUnit)
  }

  const stageUnit = store.stageUnit!

  queue = new InsertQueue(
    stageUnit.schedules,
    _global.isProd ? undefined : db.debug.insertQueueConfig
  )
  const progressCache = db.unitProgressCacheV2[QueueType.Insert]

  if (progressCache) {
    queue.setState(progressCache)
  }

  data.activeFrame = queue.currentFrame()
  data.lastCard = queue.getLastCard()
  data.practiceProgress = queue.practiceProgress

  if (queue.isDone) {
    onDone()
  }
}

const hintStyle = computed(() => {
  const left = Math.min(Math.max(data.practiceProgress - 10, 15), 85)

  return `left: ${left}%;`
})

const combo = computed(() => store.stageUnit?.currentComboCount ?? 0)

onInit(loadUnit)

function showComboHint() {
  if (combo.value > 1) {
    data.hint = `Combo x ${combo.value}`
  } else {
    data.hint = `Hit + 1`
  }

  setTimeout(() => {
    data.hint = ''
  }, 500)
}

function onMatchCorrect() {
  if (data.activeFrame?.type !== FrameType.Boss) return
  if (
    data.activeFrame.boss.cardFace?.type !== VirtualCardType.EnWordMatch &&
    data.activeFrame.boss.cardFace?.type !== VirtualCardType.KidHanziMatch
  )
    return

  const newCombo = combo.value + 1
  store.setComboCount(newCombo)
  data.activeFrame.boss.hp -= data.activeFrame.boss.tickHp
}

function onEventCollect(cardId: number | undefined, event: UnitEventType) {
  // 答对答错时震动
  if (event === UnitEventType.CORRECT) {
    vibrate(VibrateType.Success)
  }
  if (event === UnitEventType.WRONG) {
    vibrate(VibrateType.Warning)
  }
  if (!data.activeFrame || cardId == null) return

  store.insertLearningEvent(cardId, {
    event,
    timestamp: Date.now(),
  })
}

function onNext() {
  if (queue.isDone) {
    onDone()
    return
  }

  // 由于从 bossstar 进入 boss 时，lastcardbutton 组件发生了变化
  // 如果此时 showGuide 为 true 的话，直接显示会导致 tooltip 报错
  // 所以先设置为 false，等卡片显示时会走到 onCardEnter 逻辑重新显示提示
  if (data.activeFrame?.type === FrameType.BossStart) {
    data.showLastCardGuide = false
  }

  data.activeFrame = queue.currentFrame()
  data.lastCard = queue.getLastCard()
  data.cardLearnRound++

  const state = queue.getState()
  db.unitProgressCacheV2[QueueType.Insert] = state

  // 如果 boss 被打败，关闭上一张卡片的提示
  if (
    data.activeFrame?.type === FrameType.Boss &&
    data.activeFrame.boss.hp <= 0
  ) {
    data.preShowLastCardGuide = false
    data.showLastCardGuide = false
  }
}

function onEmptyFrameTick() {
  queue.tick()
  onNext()
}

function onBossDone() {
  queue.tick()
  onNext()
}

function onStar(star: FeedbackStar) {
  _am.playAudio('public_correct')

  queue.tick(star)
  data.practiceProgress = queue.practiceProgress

  if (star === FeedbackStar.Three) {
    const newCombo = combo.value + 1
    store.setComboCount(newCombo)
    showComboHint()

    // 答对时清除上一张卡片的引导提示
    data.preShowLastCardGuide = false
    data.showLastCardGuide = false
  } else {
    // 答错时尝试展示上一张卡片的引导
    tryShowLastCardGuide()
    store.setComboCount(0)
  }
}

function onDone() {
  const cardIds = (store.stageUnit?.schedules ?? []).map(item => item.cardId)

  for (const id of cardIds) {
    store.completeCard(id)
  }

  // 检测是不是每张卡片都学完了最大次数
  const allCardsCompleted = cardIds.every(id => {
    const times = queue.cardPracticed[id]
    return times === queue.config.practiceCardTimes
  })

  if (!allCardsCompleted) {
    _reportError({
      msg: 'queue end without all cards complete all times.',
      queueDebugInfo: queue.debugInfo,
      queueState: queue.getState(),
    })
  }
  completeReport()

  delete db.unitProgressCacheV2[QueueType.Insert]
}

function onLastCardShow() {
  if (data.lastCard == null) return

  if (data.showLastCardGuide) {
    data.preShowLastCardGuide = false
    data.showLastCardGuide = false
    store.completeLastCardGuide()
  }

  return _openDialog(CardDetailPopup, {
    rootClass: _global.isPcMode
      ? 'w-600px min-h-400px max-h-650px'
      : 'g-dialog',
    props: {
      card: data.lastCard,
      isFirstLearn: false,
      primaryButtonText: _t('learn.continue'),
      closeable: true,
      title: _t('learn.review_title'),
    },
    dialog: {
      showHeader: false,
      contentStyle: 'padding: 0px;',
      // TODO: tailwind css 生成不了使用 calc 的高度，这里先用 style
      style: !_global.isPcMode
        ? 'max-height: calc(var(--ld-viewport-height) - 100px);'
        : '',
    },
  })
}

function onCardEnter() {
  data.showLastCardGuide = data.preShowLastCardGuide
}

function onCardEdit() {
  // 在移动端且不在app中时，显示app下载提示
  if (_global.isMobileMode && !_global.isInsideApp) {
    showAppDownloadDialog()
    return
  }

  if (!data.activeFrame) {
    return
  }

  let currentCard: Card | undefined
  let cardId: number | undefined

  // 根据不同阶段获取当前卡片信息
  if (data.activeFrame.type === FrameType.Practice) {
    currentCard = data.activeFrame.face?.card
    cardId = data.activeFrame.face?.cardId
  } else if (
    data.activeFrame.type === FrameType.Boss &&
    data.activeFrame.boss.cardFace &&
    data.activeFrame.boss.cardFace.type !== VirtualCardType.Match
  ) {
    const cardFace = data.activeFrame.boss.cardFace as any
    currentCard = cardFace.card
    cardId = cardFace.cardId
  }

  if (!currentCard || !cardId) {
    _message.info(_t('learn.cannot_edit'))
    return
  }
  _openDialog(CardEditPopup, {
    rootClass: _global.isPcMode
      ? 'w-600px min-h-400px max-h-650px'
      : 'g-dialog',
    props: {
      card: currentCard,
      cardId: cardId,
      packageId: props.packageId,
    },
    dialog: {
      showHeader: false,
      contentStyle: 'padding: 0px;',
      style: !_global.isPcMode
        ? 'max-height: calc(var(--ld-viewport-height) - 100px);'
        : '',
    },
  }).then(async (result: unknown) => {
    if (result && data.activeFrame) {
      const editResult = result as
        | { type: 'update'; card: Card }
        | { type: 'delete' }

      if (editResult.type === 'update') {
        try {
          // 获取当前卡片ID
          let currentCardId: number | undefined
          if (data.activeFrame.type === FrameType.Practice) {
            currentCardId = data.activeFrame.cardId
          } else if (
            data.activeFrame.type === FrameType.Boss &&
            data.activeFrame.boss.cardFace &&
            data.activeFrame.boss.cardFace.type !== VirtualCardType.Match
          ) {
            const cardFace = data.activeFrame.boss.cardFace as any
            currentCardId = cardFace.cardId
          }

          if (currentCardId) {
            // 1. 更新队列中缓存的卡片数据（this.cards 数组）
            queue.updateCard(currentCardId, editResult.card)

            // 2. 刷新整个队列的 AltCards 数据（更新所有 frames 中的 altCards）
            queue.refreshAllFrames()

            // 3. 强制触发响应式更新
            const temp = data.activeFrame
            data.activeFrame = null
            await nextTick()
            data.activeFrame = temp
          }
        } catch (error) {
          console.error('Failed to update card:', error)
        }
      } else if (editResult.type === 'delete') {
        // 获取当前卡片ID
        let currentCardId: number | undefined
        if (data.activeFrame.type === FrameType.Practice) {
          currentCardId = data.activeFrame.cardId
        } else if (
          data.activeFrame.type === FrameType.Boss &&
          data.activeFrame.boss.cardFace &&
          data.activeFrame.boss.cardFace.type !== VirtualCardType.Match
        ) {
          const cardFace = data.activeFrame.boss.cardFace as any
          currentCardId = cardFace.cardId
        }

        if (currentCardId) {
          // 从队列中移除卡片
          queue.removeCard(currentCardId)

          // 更新进度条
          data.practiceProgress = queue.practiceProgress

          // 检查是否还有剩余卡片
          if (queue.getCards().length === 0) {
            // 如果没有剩余卡片，提示用户并退出学习
            await _confirm({
              scene: 'warn',
              title: _t('learn.no_cards_title'),
              content: _t('learn.no_cards_content'),
              primaryText: _t('common.confirm'),
            })

            // 直接退出学习
            emit('close')
            return
          }

          // 继续到下一个 Frame
          queue.tick()
          onNext()
        }
      }
    }
  })
}

function onHintShow() {
  if (!data.activeFrame) {
    return
  }

  let currentCard: Card | undefined
  let cardId: number | undefined
  let isFirstLearn = false

  if (data.activeFrame.type === FrameType.Practice) {
    currentCard = data.activeFrame.face?.card
    cardId = data.activeFrame.face?.cardId
    isFirstLearn = data.activeFrame.face?.isFirstLearn ?? false
  } else if (
    data.activeFrame.type === FrameType.Boss &&
    data.activeFrame.boss.cardFace &&
    data.activeFrame.boss.cardFace.type !== VirtualCardType.EnWordMatch &&
    data.activeFrame.boss.cardFace.type !== VirtualCardType.KidHanziMatch
  ) {
    // TypeScript需要类型断言，因为cardFace可能是VirtualCardMatch
    const cardFace = data.activeFrame.boss.cardFace as any
    currentCard = cardFace.card
    cardId = cardFace.cardId
    isFirstLearn = cardFace.isFirstLearn ?? false
  }

  if (!currentCard || !cardId) {
    return
  }

  // 记录查看提示的行为数据
  store.insertLearningEvent(cardId, {
    event: UnitEventType.TIP,
    timestamp: Date.now(),
  })

  return showCardDetail(currentCard, isFirstLearn, undefined, {
    buttonTextKey: 'common.got2',
  })
}

function onFeedback() {
  if (!data.activeFrame) {
    return
  }

  let currentCard: Card | undefined
  let cardId: number | undefined

  if (data.activeFrame.type === FrameType.Practice) {
    currentCard = data.activeFrame.face?.card
    cardId = data.activeFrame.face?.cardId
  } else if (
    data.activeFrame.type === FrameType.Boss &&
    data.activeFrame.boss.cardFace &&
    data.activeFrame.boss.cardFace.type !== VirtualCardType.EnWordMatch &&
    data.activeFrame.boss.cardFace.type !== VirtualCardType.KidHanziMatch
  ) {
    const cardFace = data.activeFrame.boss.cardFace as any
    currentCard = cardFace.card
    cardId = cardFace.cardId
  }

  if (!currentCard || !cardId) {
    return
  }

  // 获取当前学习单元和用户信息
  const stageUnit = store.stageUnit
  const user = store.user

  if (!stageUnit || !user) {
    _message.error('获取学习信息失败')
    return
  }

  // 获取当前卡片的作者ID
  const cardSchedule = stageUnit.schedules.find(s => s.cardId === cardId)
  const authorId = cardSchedule?.authorId || user.id?.toString() || '0'

  // 构建问卷参数
  const params = new URLSearchParams({
    prefill_lid: user.id, // 用户的id就是lid
    hide_lid: '1',
    prefill_pkgid: props.packageId?.toString() || '0',
    hide_pkgid: '1',
    prefill_pkgname: '卡包', // 暂时使用默认值，后续可能需要从其他地方获取
    hide_pkgname: '1',
    prefill_cardid: cardId.toString(),
    hide_cardid: '1',
    prefill_authorid: authorId,
    hide_authorid: '1',
  })

  const feedbackUrl = `https://qianmojiaoyu.feishu.cn/share/base/form/shrcngkW8atpXaaHy1pKqHHS0og?${params.toString()}`

  openLink(feedbackUrl)
}

// 尝试显示上一张卡片的引导提示
function tryShowLastCardGuide() {
  if (store.isLastCardGuideCompleted) {
    return
  }

  // 如果上一张卡片答错过 & 不是第一次学习，则展示上一张卡片的引导提示
  if (data.activeFrame?.type === FrameType.Practice) {
    if (!data.activeFrame.face.isFirstLearn) {
      data.preShowLastCardGuide = true
    }
  } else if (data.activeFrame?.type === FrameType.Boss) {
    const cardFace = data.activeFrame.boss.cardFace
    if (
      cardFace?.type !== VirtualCardType.EnWordMatch &&
      cardFace?.type !== VirtualCardType.KidHanziMatch &&
      'isFirstLearn' in cardFace &&
      !cardFace?.isFirstLearn
    ) {
      data.preShowLastCardGuide = true
    }
  }
}

function showAppDownloadDialog() {
  _confirm({
    scene: 'warn',
    icon: {
      type: 'svg',
      name: 'ld-avatar',
    },
    content: _t('app.download_tip2', { link: _global.pcLink }),
    primaryText: _t('app.download_tip1'),
    secondaryText: _t('common.not_yet'),
    async onPrimaryClick(resolve) {
      router.push({ name: 'app-download' })
      resolve(true)
    },
  })
}
</script>

<style scoped>
.card-slide-enter-active,
.card-slide-leave-active {
  transition: transform 300ms;
}
.card-slide-enter-from {
  transform: translateX(100%);
}
.card-slide-enter-to {
  transform: translateX(0%);
}
.card-slide-leave-from {
  transform: translateX(0%);
}
.card-slide-leave-to {
  transform: translateX(-100%);
}

.monster-hint-enter-active,
.monster-hint-leave-active {
  transition: opacity 500ms;
}

.monster-hint-enter {
  opacity: 1; /* 初始位置 */
}
.monster-hint-leave-to {
  opacity: 0; /* 最终位置 */
}

.monster-hint {
  position: absolute;
  top: 20px;
  color: var(--red-400);
  font-family: Comic Sans MS;
  font-size: 15px;
  font-weight: 400;
  transform: rotate(-5.89deg);
  z-index: 1;
  white-space: nowrap;
}
</style>
